import { createContext, useContext } from 'react';

import type { Schemas } from '@/api/client';

export type CurrentUser = Schemas['MeOut'];
export type Role = Schemas['UserRole'];

export type AuthState =
  | { status: 'loading' }
  | { status: 'anonymous' }
  | { status: 'offline' }
  | { status: 'authenticated'; user: CurrentUser };

export interface AuthContextValue {
  state: AuthState;
  /** Without `role`, a user with several roles gets RoleRequiredError. */
  login: (login: string, password: string, role?: Role) => Promise<CurrentUser>;
  /** Work in another of the user's roles: a new session replaces this one. */
  switchRole: (role: Role) => Promise<CurrentUser>;
  logout: () => Promise<void>;
  retryRestore: () => void;
}

/** The password was right, and the account has several roles: which one to work in. */
export class RoleRequiredError extends Error {
  readonly roles: Role[];

  constructor(roles: Role[]) {
    super('role required');
    this.name = 'RoleRequiredError';
    this.roles = roles;
  }
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}

/** The signed-in user; only for components rendered behind RequireAuth. */
export function useCurrentUser(): CurrentUser {
  const { state } = useAuth();
  if (state.status !== 'authenticated') throw new Error('No authenticated user');
  return state.user;
}

/** The signed-in user, or null - for parts that also render outside the sign-in (the
 * banners, STAGE_6 6.7). */
export function useOptionalUser(): CurrentUser | null {
  const value = useContext(AuthContext);
  return value?.state.status === 'authenticated' ? value.state.user : null;
}

/** An admin, or the vendor, who may do everything an admin may (STAGE_6 6.7). */
export function isAdminRole(role: Role): boolean {
  return role === 'admin' || role === 'superadmin';
}

/** Staff full name when linked, otherwise the login. */
export function displayName(user: CurrentUser): string {
  return user.full_name?.trim() || user.login;
}
